/**
 * VocabularyCatalogService — V85-1-9f / ADR-0034.
 *
 * Three canonical vocabularies referenced by obligation_json and control_specs:
 *   1. duty       — obligation_json[].duty, required_controls[]
 *   2. evidence_kind — obligation_json.required_evidence[], control_specs.accepted_signals_json
 *   3. disclosure_field — obligation_json.required_disclosures[]
 *
 * The JSON schema files in packages/schemas/vocabularies/ mirror these arrays
 * for external consumers (ajv validation, documentation). This module is the
 * runtime source of truth for packages/core.
 */
// 2026.10 (owner 2026-10-25): + GDPR duties / evidence kinds (790) and OMB M-25-21 / M-25-22 / M-26-04 (770).
// 2026.11 (owner 2026-10-31, v11.6 V116-3-4): + 9 EU AI Act duties and 2 evidence kinds (EU duty fit list).
// Additive only: every 2026.09 and 2026.10 value is still valid (vocabulary-catalog.test.ts pins both lists).
export const VOCABULARY_CATALOG_VERSION = '2026.11';
// ── Duty vocabulary ─────────────────────────────────────────────
// Values from obligation_json[].duty across all seeded rules and
// the duty-control-catalog. Sorted alphabetically.
const DUTIES = [
    'access_controls',
    'access_right',
    'accuracy_testing',
    'adverse_decision_disclosure',
    'ai_cybersecurity',
    'ai_interaction_disclosure',
    'anti_discrimination_safeguards',
    'appeal_process',
    'audit_logging',
    'bias_audit',
    'bias_evaluation',
    'breach_notification',
    'business_associate_agreement',
    'candidate_notice',
    'catastrophic_risk_assessment',
    'change_notification',
    'conformity_assessment',
    'consumer_notice',
    'copyright_policy',
    'critical_incident_reporting',
    'data_governance',
    'data_use_restriction',
    'decision_explanation',
    'deepfake_disclosure',
    'deployer_duties',
    'design_controls_820',
    'developer_documentation',
    'dsi_source_attribute_disclosure',
    'eu_database_registration',
    'eu_representative',
    'explicit_written_consent_42cfr2',
    'express_point_of_view',
    'frontier_safety_framework_publication',
    'gdpr_compliance',
    'generative_abuse_safeguards',
    'governance_body',
    'high_risk_classification_assessment',
    'human_intervention',
    'human_oversight',
    'impact_assessment',
    'intervention_risk_management',
    'llm_transparency_disclosure',
    'minimum_necessary_use',
    'minimum_risk_practices',
    'necessity_assessment',
    'operator_training',
    'opt_out_mechanism',
    'opt_out_right',
    'post_market_monitoring_plan',
    'post_market_surveillance',
    'pre_use_notice',
    'predetermined_change_control_plan',
    'prior_consultation',
    'processing_description',
    'public_feedback',
    'publish_audit_summary',
    'qualified_service_organization_agreement',
    'quality_management_system',
    'record_retention',
    'red_team_testing',
    'redisclosure_prohibition_notice',
    'responsible_official',
    'rights_risk_assessment',
    'risk_assessment',
    'risk_management_system',
    'risk_mapping',
    'risk_measurement',
    'risk_mitigation',
    'samd_risk_categorization',
    'segmented_record_access_controls',
    'special_category_condition',
    'special_category_safeguards',
    'synthetic_content_marking',
    'technical_documentation',
    'uk_gdpr_compliance',
    'uk_representative',
    'unbiased_ai_principles',
    'use_case_inventory',
    'vendor_evaluation_access',
    'vendor_lock_in_protection',
    'whistleblower_protections',
];
// ── Evidence kind vocabulary ────────────────────────────────────
// Values from obligation_json.required_evidence[],
// duty-control-catalog accepted_signals, and baseline control specs
// (migrations 163+164 OGCF + families). Sorted alphabetically.
const EVIDENCE_KINDS = [
    '42cfr2_consent_form',
    '510k_or_de_novo_or_exemption_determination',
    'abuse_detection_config',
    'acceptable_use_policy',
    'acceptance_criteria_evidence',
    'access_matrix',
    'access_review_report',
    'accountability_designation',
    'accountability_record',
    'accuracy_evaluation',
    'action_audit_log',
    'action_logging_config',
    'action_permission_map',
    'admt_pre_use_notice_template',
    'admt_risk_assessment',
    'adversarial_robustness_report',
    'adversarial_test_report',
    'adversarial_test_results',
    'adversarial_testing_report',
    'adverse_decision_record',
    'adverse_disclosure_template',
    'adverse_event_monitoring_log',
    'affected_parties_register',
    'agent_communication_protocol',
    'agent_registry',
    'ai_attack_surface_assessment',
    'ai_committee_terms_of_reference',
    'ai_impact_assessment',
    'ai_kpi_document',
    'ai_objectives_plan',
    'ai_policy_document',
    'ai_privacy_risk_report',
    'ai_roadmap',
    'ai_roles_policy',
    'ai_strategy_document',
    'ai_system_inventory',
    'alert_rule_evidence',
    'alert_rules',
    'algorithm_selection_rationale',
    'algorithmic_impact_assessment',
    'annex_iv_documentation',
    'annotation_qa_report',
    'anomaly_detection_config',
    'anonymization_report',
    'anti_bias_testing_report',
    'anti_discrimination_report',
    'anti_discrimination_safeguards_evidence',
    'appeal_process_documentation',
    'approval_workflow_log',
    'architecture_decision_record',
    'architecture_document',
    'art9_condition_record',
    'attestation_evidence',
    'audit_log_configuration',
    'audit_log_sample',
    'audit_program_plan',
    'audit_summary_publication',
    'audit_trail_sample',
    'authentication_config',
    'authorization_matrix',
    'autonomy_classification',
    'awareness_program_record',
    'baa_executed',
    'before_after_comparison',
    'behavioral_baseline',
    'benchmark_report',
    'bias_audit_report',
    'bias_aware_sampling_report',
    'bias_evaluation_report',
    'bias_mitigation_plan',
    'bias_mitigation_report',
    'bias_monitoring_config',
    'bias_monitoring_report',
    'bias_test_report',
    'board_ai_resolution',
    'bom_manifest',
    'boundary_enforcement_config',
    'boundary_monitoring_config',
    'breach_notification_procedure',
    'budget_allocation',
    'caio_designation',
    'candidate_notice_record',
    'capability_manifest',
    'carbon_footprint_estimate',
    'cascade_failure_analysis',
    'catastrophic_risk_assessment_report',
    'change_approval_record',
    'change_board_charter',
    'change_control_policy',
    'change_management_record',
    'change_notification_process',
    'change_record',
    'checkpoint_config',
    'ci_cd_evidence',
    'competence_assessment',
    'compute_report',
    'conformity_assessment_report',
    'conformity_certificate',
    'consent_management_config',
    'consent_management_record',
    'consent_record',
    'consistency_evaluation',
    'consumer_notification_record',
    'content_filter_rules',
    'content_labeling_record',
    'content_policy',
    'contest_process',
    'continuity_test_report',
    'continuous_eval_record',
    'contraindication_list',
    'control_check',
    'conversation_logging_config',
    'coordination_policy',
    'copyright_filter_config',
    'cross_border_exception_log',
    'cross_border_transfer_assessment',
    'customer_instruction_record',
    'data_classification_record',
    'data_classification_scheme',
    'data_disposition_record',
    'data_gap_report',
    'data_governance_policy',
    'data_handling_policy',
    'data_lineage_record',
    'data_localization_assessment',
    'data_minimization_evidence',
    'data_origin_documentation',
    'data_poisoning_detection_config',
    'data_protection_assessment',
    'data_provenance_record',
    'data_quality_gate_config',
    'data_quality_report',
    'data_residency_attestation',
    'data_sheet',
    'data_source_inventory',
    'data_subject_rights_process',
    'data_use_terms',
    'data_validation_results',
    'dataset_card',
    'debiasing_methodology',
    'decision_explanation_record',
    'decision_explanation_template',
    'decision_factor_documentation',
    'decision_logic_notice',
    'decision_rationale_record',
    'demographic_analysis',
    'demographic_parity_evaluation',
    'dependency_manifest',
    'deployer_compliance_record',
    'deployer_instructions',
    'deployment_context_record',
    'deployment_pattern_config',
    'deployment_plan',
    'design_rationale',
    'design_specification',
    'detection_mechanism_config',
    'developer_docs',
    'developer_documentation_package',
    'disaggregated_metrics_report',
    'disclosure_mechanism',
    'disparate_impact_analysis',
    'document_control_procedure',
    'dpa_processor_agreement',
    'dpia_report',
    'drift_detection_config',
    'drift_detection_report',
    'drift_prevention_report',
    'drift_report',
    'dsi_transparency_documentation',
    'edge_case_catalog',
    'employment_records',
    'encryption_at_rest_evidence',
    'encryption_config',
    'encryption_in_transit_evidence',
    'end_user_resources',
    'energy_consumption_record',
    'enforcement_config',
    'escalation_path_documentation',
    'escalation_test_report',
    'ethical_review_report',
    'eu_database_registration_record',
    'eu_representative_designation',
    'evaluation_access_terms',
    'evaluation_methodology',
    'evaluation_results',
    'evidence_retention_schedule',
    'explainability_documentation',
    'explanation_documentation',
    'explanation_method_evaluation',
    'failsafe_documentation',
    'failure_mode_analysis',
    'failure_mode_report',
    'failure_rate_report',
    'fairness_dashboard',
    'fairness_evaluation',
    'fairness_evaluation_report',
    'fairness_methodology',
    'fairness_metrics_report',
    'fairness_thresholds',
    'fallback_test_report',
    'feedback_channel',
    'filter_rules',
    'frontier_safety_framework_doc',
    'governance_body_charter',
    'governance_body_record',
    'governance_charter',
    'governance_committee_record',
    'governance_org_chart',
    'grounding_evidence',
    'guardrail_config',
    'hallucination_detection_config',
    'harmful_content_filter_config',
    'healthcare_disclosure_record',
    'high_impact_determinations',
    'hipaa_risk_assessment',
    'human_oversight_design_doc',
    'human_oversight_plan',
    'human_review_procedure',
    'hyperparameter_record',
    'identity_propagation_config',
    'immutable_storage_config',
    'impact_assessment',
    'impact_assessment_inputs',
    'impact_assessment_methodology',
    'impact_assessment_report',
    'impact_register',
    'incident_report_record',
    'incident_reporting_procedure',
    'incident_response_plan',
    'incident_summary_report',
    'inference_benchmark',
    'infrastructure_inventory',
    'input_validation_config',
    'integrity_verification_report',
    'intended_use_document',
    'interaction_disclosure_record',
    'interaction_notice',
    'internal_audit_report',
    'interpretability_assessment',
    'intervention_protocol',
    'intervention_risk_assessment',
    'inventory_submission',
    'ip_compliance_review',
    'ip_policy',
    'jit_grant_record',
    'jurisdiction_compliance_record',
    'kill_switch_evidence',
    'kpi_dashboard',
    'labeling_guidelines',
    'latency_profile',
    'license_inventory',
    'lifecycle_governance_policy',
    'limitations_document',
    'lineage_documentation',
    'lineage_graph',
    'log_export_certificate',
    'logging_config',
    'management_review_minutes',
    'management_review_report',
    'measurement_methodology',
    'minimum_risk_practices_record',
    'minimum_risk_record',
    'misuse_risk_analysis',
    'model_card',
    'model_documentation',
    'model_hash_record',
    'model_lineage',
    'model_provenance_record',
    'model_registry_entry',
    'model_registry_record',
    'monitoring_binding',
    'monitoring_dashboard',
    'monitoring_plan',
    'nonconformity_register',
    'nondiscrimination_policy',
    'notice_template',
    'official_appointment_record',
    'operational_runbook',
    'operator_qualification_requirements',
    'operator_training_record',
    'opt_out_mechanism_evidence',
    'opt_out_mechanism_log',
    'opt_out_process_documentation',
    'opt_out_record',
    'out_of_scope_documentation',
    'output_analysis_report',
    'output_feedback_channel',
    'output_filter_config',
    'oversight_plan',
    'oversight_procedure',
    'pam_session_log',
    'pccp_documentation',
    'pedigree_document',
    'penetration_test_report',
    'performance_metrics_report',
    'performance_monitoring_config',
    'performance_monitoring_dashboard',
    'performance_report',
    'phi_access_log',
    'phi_handling_config',
    'pii_handling_policy',
    'poisoning_scan_report',
    'policy_document',
    'portability_plan',
    'post_market_monitoring_plan',
    'pre_deploy_bias_assessment',
    'pre_deploy_checklist',
    'pre_deployment_test_report',
    'predictive_model_source_attributes',
    'preprocessing_pipeline_doc',
    'privacy_controls_documentation',
    'privacy_impact_assessment',
    'prompt_injection_defense_config',
    'prompt_version_record',
    'protected_attribute_analysis',
    'provenance_attestation',
    'provenance_marking_evidence',
    'provenance_record',
    'provenance_trail',
    'published_audit_summary',
    'purpose_log',
    'qms_manual',
    'qsoa_executed',
    'quality_report',
    'raci_matrix',
    'rag_governance_policy',
    'rate_limit_config',
    'rbac_policy',
    're_evaluation_trigger_documentation',
    'records_management_policy',
    'records_of_processing',
    'recovery_procedure',
    'red_team_report',
    'redisclosure_notice_template',
    'region_configuration_export',
    'regulatory_mapping',
    'regulatory_reporting_procedure',
    'reliability_assessment',
    'representativeness_assessment',
    'residual_risk_acceptance',
    'resilience_plan',
    'resource_plan',
    'resource_requirements_doc',
    'responsible_official_designation',
    'retention_policy',
    'retention_policy_record',
    'retention_schedule',
    'retirement_plan',
    'retirement_procedure',
    'retirement_record',
    'retraining_approval_record',
    'retraining_governance_policy',
    'review_action_items',
    'review_completion_record',
    'rights_request_log',
    'risk_management_plan',
    'risk_map',
    'risk_map_document',
    'risk_measurement_report',
    'risk_metrics',
    'risk_metrics_report',
    'risk_practice_checklist',
    'risk_practice_documentation',
    'risk_register',
    'risk_treatment_effectiveness_review',
    'risk_treatment_plan',
    'robustness_evaluation',
    'robustness_test_evidence',
    'role_assignment_record',
    'rollback_procedure',
    'rollback_test_evidence',
    'rotation_evidence',
    'safeguard_documentation',
    'safety_filter_rules',
    'safety_report',
    'safety_risk_assessment',
    'samd_risk_category_memo',
    'sampling_methodology',
    'sbom_document',
    'scope_document',
    'scope_policy',
    'secret_scan_report',
    'security_test_report',
    'segmentation_access_log',
    'session_recording',
    'shadow_deployment_evidence',
    'sigstore_bundle',
    'societal_impact_assessment',
    'sod_matrix_document',
    'sod_violation_report',
    'sso_config',
    'stakeholder_analysis',
    'stakeholder_engagement_record',
    'stakeholder_fairness_input',
    'stakeholder_report',
    'stakeholder_transition_notice',
    'statistical_analysis_plan',
    'subgroup_analysis',
    'supervisory_authority_consultation',
    'supplier_access_review_report',
    'supplier_assessment',
    'supply_chain_risk_assessment',
    'synthetic_marking_evidence',
    'synthetic_media_disclosure',
    'system_documentation',
    'system_prompt_policy',
    'tabletop_exercise_report',
    'task_authorization_list',
    'technical_documentation_package',
    'technical_specification',
    'test_gate_config',
    'test_report',
    'test_set_documentation',
    'test_suite_results',
    'testing_procedure_documentation',
    'third_party_ai_inventory',
    'third_party_review',
    'tier_assessment',
    'tool_registry',
    'training_data_documentation',
    'training_data_license_audit',
    'training_plan',
    'transfer_mechanism',
    'transparency_report',
    'triage_procedure',
    'uk_representative_designation',
    'use_case_boundary_document',
    'use_case_inventory_record',
    'use_case_register',
    'use_case_specification',
    'user_study_report',
    'validation_results',
    'validation_test_report',
    'vault_config',
    'vdp_record',
    'vendor_assessment_report',
    'version_logging_config',
    'vulnerability_disclosure_policy',
    'watermark_config',
    'whistleblower_policy',
];
// ── Disclosure field vocabulary ─────────────────────────────────
// Values from obligation_json.required_disclosures[] and
// ai-disclosure-service content groups. Sorted alphabetically.
const DISCLOSURE_FIELDS = [
    'admt_use_in_significant_decision_disclosed_to_consumer',
    'ai_use_in_phi_processing_disclosed_to_patient',
    'data_lineage',
    'data_sources',
    'evidence_refs',
    'frameworks_claimed',
    'human_oversight',
    'incident',
    'models',
    'providers',
    'retention_and_training_use',
    'scope',
    'training_data_classes',
];
export const VALID_DUTIES = new Set(DUTIES);
export const VALID_EVIDENCE_KINDS = new Set(EVIDENCE_KINDS);
export const VALID_DISCLOSURE_FIELDS = new Set(DISCLOSURE_FIELDS);
export const ALL_DUTIES = DUTIES;
export const ALL_EVIDENCE_KINDS = EVIDENCE_KINDS;
export const ALL_DISCLOSURE_FIELDS = DISCLOSURE_FIELDS;
export const EMPTY_VOCABULARY_OVERLAY = { duty: new Set(), evidence_kind: new Set(), disclosure_field: new Set() };
/** The code catalog for one vocabulary (no overlay): a value here can never be a `vocabulary_terms` row. */
export function codeVocabulary(vocabulary) {
    return vocabulary === 'duty' ? VALID_DUTIES : vocabulary === 'evidence_kind' ? VALID_EVIDENCE_KINDS : VALID_DISCLOSURE_FIELDS;
}
export function validateDuty(value, context, overlay) {
    if (VALID_DUTIES.has(value) || overlay?.duty.has(value))
        return null;
    return { vocabulary: 'duty', value, context: context ?? 'unknown' };
}
export function validateEvidenceKind(value, context, overlay) {
    if (VALID_EVIDENCE_KINDS.has(value) || overlay?.evidence_kind.has(value))
        return null;
    return { vocabulary: 'evidence_kind', value, context: context ?? 'unknown' };
}
export function validateDisclosureField(value, context, overlay) {
    if (VALID_DISCLOSURE_FIELDS.has(value) || overlay?.disclosure_field.has(value))
        return null;
    return { vocabulary: 'disclosure_field', value, context: context ?? 'unknown' };
}
/**
 * Validate all vocabulary references in an obligation_json block.
 * Used by rule apply and obligation-set assembly.
 */
export function validateObligationVocabulary(obligationJson, ruleContext, overlay) {
    const violations = [];
    if (obligationJson.duty) {
        const v = validateDuty(obligationJson.duty, `${ruleContext}.duty`, overlay);
        if (v)
            violations.push(v);
    }
    if (obligationJson.required_controls) {
        for (const ctrl of obligationJson.required_controls) {
            const v = validateDuty(ctrl, `${ruleContext}.required_controls`, overlay);
            if (v)
                violations.push(v);
        }
    }
    if (obligationJson.required_evidence) {
        for (const ek of obligationJson.required_evidence) {
            const v = validateEvidenceKind(ek, `${ruleContext}.required_evidence`, overlay);
            if (v)
                violations.push(v);
        }
    }
    if (obligationJson.required_disclosures) {
        for (const df of obligationJson.required_disclosures) {
            const v = validateDisclosureField(df, `${ruleContext}.required_disclosures`, overlay);
            if (v)
                violations.push(v);
        }
    }
    return { valid: violations.length === 0, violations };
}
/**
 * Validate accepted_signals in a control_spec row.
 * Used by control-spec apply and gate eval.
 */
export function validateAcceptedSignals(signals, specContext, overlay) {
    const violations = [];
    // A signal is a kind string or `{ kind, … }` (e.g. AS-2/AS-4 2.0.0's `{ kind: 'control_check' }`).
    for (const signal of signals) {
        const kind = typeof signal === 'object' && signal !== null ? signal.kind : signal;
        if (typeof kind !== 'string' || !kind)
            continue;
        const v = validateEvidenceKind(kind, `${specContext}.accepted_signals`, overlay);
        if (v)
            violations.push(v);
    }
    return { valid: violations.length === 0, violations };
}
/**
 * Format violations into a stable, human-readable error message.
 */
export function formatViolations(violations) {
    return violations
        .map((v) => `unknown ${v.vocabulary} '${v.value}' in ${v.context}`)
        .join('; ');
}
//# sourceMappingURL=vocabulary-catalog-service.js.map